import { useState, useEffect, useCallback } from "react";
import { C, S } from "./theme";
import { StatusBadge } from "./StatusBadge";
import { GetStartedCTA } from "../components/DemoDataControls";
import {
  listCustomers, getCustomer, createCustomer, updateCustomer, deleteCustomer,
  CUSTOMER_TYPES, CUSTOMER_STATUSES,
} from "../lib/customers";
import CustomerForm from "./CustomerForm";
import CustomerView from "./CustomerView";

const typeLabel = (v) => CUSTOMER_TYPES.find((t) => t.value === v)?.label || v || "—";
const primaryOf = (c) => (c.customer_contacts || []).find((p) => p.is_primary) || (c.customer_contacts || [])[0] || null;
const cityOf = (c) => {
  const a = (c.customer_addresses || []);
  const d = a.find((x) => x.is_default) || a[0];
  return d?.city || "";
};

export default function CustomerManager({ autoNew, onAutoNew, onOpenStatement, openId, onOpenHandled }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const [editing, setEditing] = useState(null);   // customer object or {} for new, or null
  const [viewing, setViewing] = useState(null);    // full customer object or null

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listCustomers({ search, status: statusFilter, type: typeFilter }));
    } catch (e) {
      setError(e.message || "Could not load customers.");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, typeFilter]);

  useEffect(() => {
    const t = setTimeout(load, 200); // debounce search/filter changes
    return () => clearTimeout(t);
  }, [load]);

  // Quick-action "New Customer" deep link from the dashboard.
  useEffect(() => {
    if (autoNew) { setEditing({}); onAutoNew && onAutoNew(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoNew]);

  // Deep link: open a specific customer's view (e.g. from Customer Intelligence).
  useEffect(() => {
    if (!openId) return;
    (async () => {
      try { const c = await getCustomer(openId); if (c) setViewing(c); }
      catch (e) { setError(e.message); } finally { onOpenHandled && onOpenHandled(); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  const openNew = () => setEditing({});
  const openEdit = async (c) => {
    // Load the full record (all children) before editing.
    try { setEditing(await getCustomer(c.id)); }
    catch (e) { setError(e.message); }
  };
  const openView = async (c) => {
    try { setViewing(await getCustomer(c.id)); }
    catch (e) { setError(e.message); }
  };

  const save = async (payload) => {
    if (editing && editing.id) await updateCustomer(editing.id, payload);
    else await createCustomer(payload);
    setEditing(null);
    await load();
  };

  const del = async (c) => {
    if (!window.confirm(`Delete ${c.company_name}?\nThis also removes its contacts, addresses and timeline.`)) return;
    try { await deleteCustomer(c.id); await load(); }
    catch (e) { setError(e.message); }
  };

  const selStyle = { ...S.input, width: "auto", minWidth: 130 };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input style={S.searchBar} placeholder="Search name, number, VAT, contact…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select style={selStyle} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          {CUSTOMER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select style={selStyle} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">All types</option>
          {CUSTOMER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ color: C.textMuted, fontSize: 12 }}>{rows.length} customer{rows.length === 1 ? "" : "s"}</span>
        <button style={S.btn()} onClick={openNew}>+ New Customer</button>
      </div>

      {error && <div style={{ color: C.danger, fontWeight: 600, fontSize: 13, marginBottom: 12 }}>⚠ {error}</div>}

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Number</th>
              <th style={S.th}>Company</th>
              <th style={S.th}>Type</th>
              <th style={S.th}>Primary Contact</th>
              <th style={S.th}>City</th>
              <th style={S.th}>Status</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={S.empty}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} style={S.empty}>
                <GetStartedCTA onLoaded={load} fallback={<div>No customers found. Click “+ New Customer” to add one.</div>} />
              </td></tr>
            ) : rows.map((c) => {
              const p = primaryOf(c);
              return (
                <tr key={c.id} style={{ cursor: "pointer" }} onClick={() => openView(c)}>
                  <td style={S.td}><span style={{ color: C.textMuted, fontFamily: "monospace", fontSize: 12 }}>{c.customer_number}</span></td>
                  <td style={S.td}>
                    <strong>{c.company_name}</strong>
                    {c.trading_name && <div style={{ color: C.textMuted, fontSize: 12 }}>t/a {c.trading_name}</div>}
                  </td>
                  <td style={S.td}>{typeLabel(c.customer_type)}</td>
                  <td style={S.td}>
                    {p ? (
                      <div>
                        <div>{`${p.first_name || ""} ${p.last_name || ""}`.trim() || "—"}</div>
                        {p.email && <div style={{ color: C.textMuted, fontSize: 12 }}>{p.email}</div>}
                      </div>
                    ) : <span style={{ color: C.textDim }}>—</span>}
                  </td>
                  <td style={S.td}>{cityOf(c) || <span style={{ color: C.textDim }}>—</span>}</td>
                  <td style={S.td}><StatusBadge status={c.status} /></td>
                  <td style={S.td} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button style={S.btn("ghost")} title="Edit" onClick={() => openEdit(c)}>✎</button>
                      <button style={S.btn("ghost")} title="Delete" onClick={() => del(c)}>🗑</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <CustomerForm initial={editing} onCancel={() => setEditing(null)} onSave={save} />
      )}
      {viewing && (
        <CustomerView
          customer={viewing}
          onClose={() => setViewing(null)}
          onEdit={(c) => { setViewing(null); openEdit(c); }}
          onStatement={onOpenStatement ? (c) => { setViewing(null); onOpenStatement(c.id); } : undefined}
        />
      )}
    </div>
  );
}
